import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../utilis/api";
import { loginAdmin } from "../redux/store/userSlice";
import { isTokenExpired } from "../utilis/auth";

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminSession = useSelector((state) => state.user?.admin) || {};

  // Redirect if already logged in as admin
  React.useEffect(() => {
    if (adminSession.isLoggedIn && adminSession.token && !isTokenExpired(adminSession.token)) {
      navigate("/dashboard", { replace: true });
    }
  }, [adminSession, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form data
      if (!formData.email?.trim() || !formData.password?.trim()) {
        toast.error("Please fill in all fields", { autoClose: 3000 });
        return;
      }

      // Make API request
      const response = await api.post("/auth/sign-in", {
        email: formData.email.trim(),
        password: formData.password
      });

      if (response.data?.meta?.success) {
        const user = response.data.data.user;
        const token = response.data.data.token;

        // Validate user role
        if (user.role !== "admin") {
          toast.error("Access denied. Admin privileges required.", { autoClose: 3000 });
          return;
        }

        // Validate token
        if (!token) {
          toast.error("Authentication token not received", { autoClose: 3000 });
          return;
        }

        // Dispatch login action
        dispatch(loginAdmin({ user, token }));

        toast.success(`Welcome back, ${user.name}!`, { autoClose: 2000 });
        
        // Navigate to dashboard
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 100);
        
      } else {
        const errorMessage = response.data?.meta?.message || "Login failed. Please try again.";
        toast.error(errorMessage, { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Admin login error:', error);
      const message = error?.response?.data?.meta?.message || 
                    error?.response?.data?.message ||
                    error.message || 
                    "Network error. Please check your connection.";
      toast.error(message, { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Admin Login
              </h1>
              <p className="text-gray-600 text-sm">
                Enter your credentials to access the admin panel
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@example.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
                  required
                />
              </div>

              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white font-semibold py-2.5 rounded-lg transition duration-200 mt-6"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {/* Footer Message */}
            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                Only authorized administrators can access this panel
              </p>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-xs text-center">
              🔒 This is a secure admin panel. Keep your credentials confidential.
            </p>
          </div>
        </div>
    </div>
  );
};

export default AdminLogin;
